/**
 * EPC069-12 (GiroCode / EPC QR) payload for SEPA Credit Transfer initiation.
 * Version 002, UTF-8 — BIC optional for EEA IBANs.
 */
import { isValidBic, isValidIban, normalizeBic, normalizeIban } from './iban';

export const EPC_QR_MAX_PAYLOAD_BYTES = 331;
export const EPC_QR_NAME_MAX_CHARS = 70;
export const EPC_QR_REMITTANCE_TEXT_MAX_CHARS = 140;

export type BuildEpcQrPayloadInput = {
  /** Beneficiary name (Kontoinhaber or company). */
  name: string;
  iban: string;
  /** Amount in EUR; must be > 0. */
  amountEur: number;
  /** Optional BIC (version 002). */
  bic?: string | null;
  /** Unstructured remittance (e.g. invoice number). Mutually exclusive with structured RF. */
  remittanceText?: string | null;
};

export type BuildEpcQrPayloadResult =
  | { ok: true; payload: string }
  | { ok: false; reason: string };

function truncateChars(value: string, max: number): string {
  if (value.length <= max) return value;
  return value.slice(0, max);
}

/** EPC amount: EUR + decimal with `.`, always two fraction digits. */
export function formatEpcAmount(amountEur: number): string {
  const rounded = Math.round(amountEur * 100) / 100;
  return `EUR${rounded.toFixed(2)}`;
}

export function documentTypeSupportsPaymentQr(label: string | null | undefined): boolean {
  const t = String(label ?? '').trim();
  if (t === 'Rechnung' || t === 'Abschlagsrechnung' || t === 'Schlussrechnung') return true;
  if (/Mahnung/i.test(t) || t === 'Erinnerung') return true;
  return false;
}

/**
 * Build an EPC069-12 v002 UTF-8 payload.
 * Empty optional fields keep their line; trailing empty lines after the last
 * populated field are omitted (no separator after the last populated element).
 */
export function buildEpcQrPayload(input: BuildEpcQrPayloadInput): BuildEpcQrPayloadResult {
  const name = truncateChars(String(input.name ?? '').trim().replace(/[\r\n]+/g, ' '), EPC_QR_NAME_MAX_CHARS);
  if (!name) return { ok: false, reason: 'Empfängername fehlt.' };

  const iban = normalizeIban(input.iban);
  if (!isValidIban(iban)) return { ok: false, reason: 'IBAN ungültig.' };

  const amount = Number(input.amountEur);
  if (!Number.isFinite(amount) || amount < 0.01 || amount > 999_999_999.99) {
    return { ok: false, reason: 'Betrag ungültig.' };
  }

  let bic = '';
  const rawBic = String(input.bic ?? '').trim();
  if (rawBic) {
    const normalized = normalizeBic(rawBic);
    if (!isValidBic(normalized)) return { ok: false, reason: 'BIC ungültig.' };
    bic = normalized;
  }

  const remittanceText = truncateChars(
    String(input.remittanceText ?? '')
      .trim()
      .replace(/[\r\n]+/g, ' '),
    EPC_QR_REMITTANCE_TEXT_MAX_CHARS,
  );

  // Lines 1–12 per EPC069-12; we omit trailing empty lines after last populated field.
  const lines = [
    'BCD',
    '002',
    '1', // UTF-8
    'SCT',
    bic,
    name,
    iban,
    formatEpcAmount(amount),
    '', // purpose (AT-44) unused
    '', // structured remittance unused (XOR with unstructured)
    remittanceText,
    // beneficiary-to-originator information omitted when empty
  ];

  while (lines.length > 0 && lines[lines.length - 1] === '') {
    lines.pop();
  }

  const payload = lines.join('\n');
  const bytes = new TextEncoder().encode(payload).length;
  if (bytes > EPC_QR_MAX_PAYLOAD_BYTES) {
    return { ok: false, reason: `Payload überschreitet ${EPC_QR_MAX_PAYLOAD_BYTES} Bytes.` };
  }

  return { ok: true, payload };
}
